import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { Pagination } from 'global';
import { Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import ReactTable, { Column, TableProps } from 'react-table';
import { usePagination } from 'components/shared/Table/hooks';
import { CustomPagination } from 'components/Table/Pagination';
import { useFilteredData } from 'components/shared/Table/hooks';

interface Props<TableData> extends Partial<Omit<TableProps<TableData>, 'data'>> {
  minHeight?: string;
  height?: string;
  noData?: ReactNode;
  tableData: TableData[];
  withPagination?: boolean;
  columns: Column<TableData>[];
  paginationOptions?: Partial<Omit<Pagination, 'total'>>;
}

export const TableView = <TableData extends any>({
  noData,
  columns,
  loading,
  tableData,
  getTrProps,
  minRows = 5,
  loadingText,
  height = '',
  minHeight = '',
  SubComponent,
  withPagination = true,
  paginationOptions = {},
}: Props<TableData>) => {
  const classes = useStyles({ minHeight, height });

  const paginationProps = usePagination(tableData.length, paginationOptions);
  const { filteredData, onFilteredChange } = useFilteredData<TableData>(paginationProps, tableData);

  return (
    <ReactTable<TableData>
      minRows={minRows}
      loading={loading}
      columns={columns}
      getTrProps={getTrProps}
      SubComponent={SubComponent}
      data={loading ? [] : filteredData}
      onFilteredChange={onFilteredChange}
      showPaginationBottom={withPagination}
      className={clsx(classes.table)}
      PaginationComponent={withPagination ? CustomPagination : undefined}
      loadingText={
        <Typography component="span" variant="h6">
          {loadingText ?? 'Data loading...'}
        </Typography>
      }
      noDataText={
        !loading && (
          <Typography component="span" variant="h6">
            {noData ?? 'No data'}
          </Typography>
        )
      }
      {...(withPagination && paginationProps)}
    />
  );
};

const useStyles = makeStyles<Theme, { minHeight: string; height: string }>({
  table: {
    minHeight: ({ minHeight }) => minHeight,
    height: ({ height }) => height,

    '& .-loading > div': {
      color: 'black',
    },

    '& .rt-noData': {
      top: '50%',
      left: '50%',
      color: 'black',
      transform: 'translate(-50%, -50%)',
    },

    '& .rt-table': {
      overflow: 'auto',
    },

    '& .rt-tbody': {
      overflow: 'visible !important',
    },

    '& .rt-tr-group': {
      overflow: 'visible',
    },

    '& .rt-td': {
      overflow: 'visible',
    },

    '& .customHeader': {
      margin: 0,
    },

    '& .customHeader > div': {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '18px',
      fontFamily: 'Roboto',
    },

    '& .customHeader > div::after': {
      color: 'black',
      fontWeight: 500,
    },

    '& .rt-thead.-filters input': {
      backgroundImage: 'none',
    },

    '& .rt-thead.-filters input:focus': {
      backgroundImage: 'none',
    },
  },
});
